<template>
  <profile-edit-section-card
    v-if="localState"
    title="Experience"
    breadcrumb-label="Profile"
    :saving="isSaving"
    :has-unsaved-changes="hasUnsavedChanges"
    :has-invalid-input="hasInvalidInput"
    @breadcrumb-click="cancelEdit"
    @save="handleSave"
    @cancel="cancelEdit"
  >
    <poppins-text-field
      id="years-experience"
      v-model="localState.yearsExperience"
      label-name="Years of paid pet care experience"
      type="text"
      pattern="[0-9]*"
      inputmode="numeric"
      :limit-input-to-pattern="true"
      :helper-text="yearsExperienceHelperText"
      :status="yearsExperienceStatus"
      class="mb-24"
    />
    <fieldset>
      <legend class="form__label pb-8">Which pets do you have experience caring for? (optional)</legend>
      <ul>
        <li v-for="option in petCareExperienceOptions" :key="option.value" class="mb-8">
          <poppins-checkbox v-model="localState.experience" :label-name="option.label" :value="option.value" />
        </li>
      </ul>
    </fieldset>
  </profile-edit-section-card>
</template>

<script lang="ts">
import PoppinsTextField from '@/components/textfield/TextField.vue';
import PoppinsCheckbox from '@/components/checkbox/Checkbox.vue';
import ProfileEditSectionCard from '../../components/ProfileEditSectionCard.vue';
import profileEditMixin from '../../mixins/profileEditMixin';
import { PET_CARE_SERVICES_EXPERIENCE_OPTIONS } from '../../__types__/CareTypeFormOptions';
import type { PetCareProfileFormState } from '../../__types__/PetCareProfileTypes';
import HelperStatus from '@/helpers/status';

export default {
  name: 'PetCareExperienceForm',
  components: {
    ProfileEditSectionCard,
    PoppinsTextField,
    PoppinsCheckbox,
  },
  mixins: [profileEditMixin],
  data(): any {
    return {
      localState: null,
      originalState: null,
      hasInvalidInput: false,
      yearsExperienceStatus: HelperStatus.Neutral,
    };
  },
  computed: {
    petCareExperienceOptions: () => PET_CARE_SERVICES_EXPERIENCE_OPTIONS,
    hasUnsavedChanges(): boolean {
      if (!this.localState || !this.originalState) return false;
      return JSON.stringify(this.localState) !== JSON.stringify(this.originalState);
    },
    yearsExperienceHelperText(): string {
      if (!this.hasInvalidInput) return '';
      if (!this.localState?.yearsExperience) {
        return 'Years of experience is required';
      }
      if (isNaN(Number(this.localState.yearsExperience)) || Number(this.localState.yearsExperience) <= 0) {
        return 'Years of experience must be at least 1';
      }
      if (Number(this.localState.yearsExperience) > 30) {
        return 'Years of experience cannot be over 30';
      }
      return '';
    },
  },
  watch: {
    $route() {
      this.initializeFormState();
    },
    formState: {
      handler(newState) {
        if (newState) {
          this.initializeFormState();
        }
      },
      immediate: true,
    },
    'localState.yearsExperience': function (newValue) {
      if (newValue && this.yearsExperienceStatus === HelperStatus.Error) {
        this.yearsExperienceStatus = HelperStatus.Neutral;
        this.hasInvalidInput = false;
      }
    },
  },
  mounted() {
    this.initializeFormState();
  },
  methods: {
    initializeFormState() {
      const state = this.formState as PetCareProfileFormState | null;
      if (!state) return;

      this.localState = {
        yearsExperience: String(state.yearsExperience ?? 0),
        experience: [...(state.experience || [])],
      };
      this.originalState = JSON.parse(JSON.stringify(this.localState));
    },
    async handleSave() {
      if (!this.localState) return;
      if (
        !this.localState.yearsExperience ||
        isNaN(Number(this.localState.yearsExperience)) ||
        Number(this.localState.yearsExperience) <= 0 ||
        Number(this.localState.yearsExperience) > 30
      ) {
        this.yearsExperienceStatus = HelperStatus.Error;
        this.hasInvalidInput = true;
        this.$nextTick(() => {
          const element = document.getElementById('years-experience');
          if (element) {
            element.scrollIntoView({ behavior: 'smooth', block: 'center' });
          }
        });
        return;
      }
      if (this.hasUnsavedChanges) {
        await this.saveSection({
          yearsExperience: Number(this.localState.yearsExperience),
          experience: [...this.localState.experience],
        });
      }
    },
  },
};
</script>
